import React, { useState, useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMapEvents } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import axios from "axios";
import { toast } from "sonner";
import { Navigation, MapPin, CheckCircle, Car } from "lucide-react";

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api";

// Fix leaflet icon paths
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png")
});

const LocationPicker = ({ onLocationSelect, selectingMode }) => {
    useMapEvents({
        click(e) {
            if (selectingMode) {
                onLocationSelect(e.latlng);
            }
        },
    });
    return null;
};

export const EyTaxiBookingPage = () => {
    const [pickup, setPickup] = useState(null);
    const [dropoff, setDropoff] = useState(null);
    const [selectingMode, setSelectingMode] = useState("pickup"); // 'pickup' | 'dropoff' | null
    const [estimate, setEstimate] = useState(null);
    const [name, setName] = useState("");
    const [phone, setPhone] = useState("");
    const [pickupAddress, setPickupAddress] = useState("");
    const [dropoffAddress, setDropoffAddress] = useState("");
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (pickup && dropoff) {
            fetchEstimate();
        }
    }, [pickup, dropoff]);

    const fetchEstimate = async () => {
        try {
            const res = await axios.post(`${API}/eytaxi/estimate-fare`, {
                pickup_lat: pickup.lat,
                pickup_lng: pickup.lng,
                dropoff_lat: dropoff.lat,
                dropoff_lng: dropoff.lng
            });
            if (res.data.success) {
                setEstimate(res.data);
            }
        } catch (e) {
            toast.error("Pa ka kalkile pri kous la. Eseye ankò.");
        }
    };

    const handleRequestRide = async (e) => {
        e.preventDefault();
        if (!pickup || !dropoff) return toast.error("Chwazi kote w ye ak kote w prale sou kat la.");
        if (!name || !phone) return toast.error("Tanpri mete non w ak nimewo telefòn ou.");
        
        setLoading(true);
        try {
            const res = await axios.post(`${API}/eytaxi/request-ride`, {
                passenger_name: name,
                passenger_phone: phone,
                pickup_lat: pickup.lat,
                pickup_lng: pickup.lng,
                pickup_address: pickupAddress || "Sou Kat la",
                dropoff_lat: dropoff.lat,
                dropoff_lng: dropoff.lng,
                dropoff_address: dropoffAddress || "Sou Kat la",
                estimated_fare: estimate.estimated_fare,
                distance_km: estimate.distance_km
            });
            if (res.data.success) {
                toast.success("Kòmand EyTaxiDriver ou an voye ak siksè!");
                // Reset form
                setPickup(null);
                setDropoff(null);
                setEstimate(null);
                setName("");
                setPhone("");
                setPickupAddress("");
                setDropoffAddress("");
                setSelectingMode("pickup");
            }
        } catch (e) {
            toast.error("Erè lè n ap voye kòmand lan.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F8F9FA] pb-20">
            {/* Header Area */}
            <div className="bg-[#232F3E] text-white pt-24 pb-10 px-4 rounded-b-[40px] shadow-xl">
                <div className="max-w-2xl mx-auto text-center">
                    <div className="w-16 h-16 bg-blue-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg rotate-3">
                        <Car className="w-8 h-8 text-white" />
                    </div>
                    <h1 className="text-3xl font-black italic tracking-tighter uppercase mb-2">EyTaxiDriver</h1>
                    <p className="text-sm font-bold text-blue-400 uppercase tracking-widest">Woulib an sekirite, nenpòt kilè</p>
                </div>
            </div>

            <div className="max-w-4xl mx-auto px-4 -mt-6 relative z-10">
                <div className="bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row">
                    
                    {/* Left: Form & Actions */}
                    <div className="p-6 md:p-8 md:w-1/3 flex flex-col justify-between border-b md:border-b-0 md:border-r border-gray-100">
                        <div className="space-y-6">
                            <h2 className="font-black text-xl italic uppercase tracking-tighter text-gray-800">1. Chwazi Kote w</h2>
                            
                            <div className="space-y-4">
                                <button 
                                    type="button"
                                    onClick={() => setSelectingMode("pickup")}
                                    className={`w-full p-4 rounded-2xl flex items-center justify-between transition-all ${selectingMode === "pickup" ? "bg-blue-500 text-white shadow-md scale-105" : pickup ? "bg-green-50 text-green-700" : "bg-gray-50 text-gray-600 hover:bg-gray-100"}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${selectingMode === "pickup" ? "bg-white/20" : pickup ? "bg-green-200" : "bg-gray-200"}`}>
                                            <MapPin className="w-4 h-4" />
                                        </div>
                                        <div className="text-left">
                                            <p className="text-[10px] font-black uppercase tracking-widest opacity-80">Kote w ye a</p>
                                            <p className="text-sm font-bold">{pickup ? "Makè a Plase" : "Klike sou kat la"}</p>
                                        </div>
                                    </div>
                                    {pickup && <CheckCircle className="w-5 h-5" />}
                                </button>

                                <button 
                                    type="button"
                                    onClick={() => setSelectingMode("dropoff")}
                                    className={`w-full p-4 rounded-2xl flex items-center justify-between transition-all ${selectingMode === "dropoff" ? "bg-[#FF9900] text-black shadow-md scale-105" : dropoff ? "bg-green-50 text-green-700" : "bg-gray-50 text-gray-600 hover:bg-gray-100"}`}
                                >
                                    <div className="flex items-center gap-3">
                                        <div className={`w-8 h-8 rounded-full flex items-center justify-center ${selectingMode === "dropoff" ? "bg-black/10" : dropoff ? "bg-green-200" : "bg-gray-200"}`}>
                                            <Navigation className="w-4 h-4" />
                                        </div>
                                        <div className="text-left">
                                            <p className="text-[10px] font-black uppercase tracking-widest opacity-80">Kote w prale</p>
                                            <p className="text-sm font-bold">{dropoff ? "Makè a Plase" : "Klike sou kat la"}</p>
                                        </div>
                                    </div>
                                    {dropoff && <CheckCircle className="w-5 h-5" />}
                                </button>
                            </div>

                            {estimate && (
                                <div className="bg-slate-800 rounded-2xl p-6 text-white text-center animate-in fade-in zoom-in duration-300 shadow-xl mt-4 relative overflow-hidden group">
                                    <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl -mr-16 -mt-16 group-hover:bg-blue-500/30 transition-all"></div>
                                    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1">Pri Kous la (Apeprè)</p>
                                    <h3 className="text-4xl font-black italic">{estimate.estimated_fare} <span className="text-lg text-[#FF9900] not-italic">HTG</span></h3>
                                    <p className="text-xs text-slate-400 mt-2">Distans: {estimate.distance_km} km</p>
                                </div>
                            )}
                        </div>

                        {estimate && (
                            <form onSubmit={handleRequestRide} className="mt-8 space-y-4 animate-in slide-in-from-bottom-4">
                                <div>
                                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Non w</label>
                                    <input 
                                        type="text" 
                                        required
                                        value={name}
                                        onChange={e => setName(e.target.value)}
                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Ekri non w..."
                                    />
                                </div>
                                <div>
                                    <label className="text-[10px] font-black text-gray-500 uppercase tracking-widest">Telefòn</label>
                                    <input 
                                        type="tel" 
                                        required
                                        value={phone}
                                        onChange={e => setPhone(e.target.value)}
                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3 text-sm font-bold focus:outline-none focus:ring-2 focus:ring-blue-500"
                                        placeholder="Ekri nimewo w..."
                                    />
                                </div>
                                
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full bg-blue-500 hover:bg-blue-600 text-white rounded-xl py-4 font-black uppercase tracking-widest italic flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                                >
                                    {loading ? "N ap chèche chofè..." : "Kòmande Kounea"}
                                    {!loading && <Car className="w-5 h-5" />}
                                </button>
                            </form>
                        )}
                    </div>

                    {/* Right: Map */}
                    <div className="md:w-2/3 h-[500px] md:h-auto relative z-0">
                        {selectingMode && (
                            <div className="absolute top-4 left-1/2 transform -translate-x-1/2 z-[1000] bg-black/80 text-white px-6 py-2 rounded-full font-bold text-xs uppercase tracking-widest animate-pulse shadow-lg backdrop-blur-sm border border-white/10">
                                Klike sou kat la pou plase makè a
                            </div>
                        )}
                        <MapContainer 
                            center={[18.5333, -72.3333]} // Port-au-Prince default
                            zoom={13} 
                            style={{ height: '100%', width: '100%', minHeight: '500px' }}
                        >
                            <TileLayer
                                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            />
                            <LocationPicker 
                                selectingMode={selectingMode}
                                onLocationSelect={(latlng) => {
                                    if (selectingMode === 'pickup') {
                                        setPickup(latlng);
                                        setSelectingMode('dropoff');
                                    } else if (selectingMode === 'dropoff') {
                                        setDropoff(latlng);
                                        setSelectingMode(null);
                                    }
                                }} 
                            />
                            {pickup && (
                                <Marker position={pickup}>
                                    <Popup>Kote w ye a</Popup>
                                </Marker>
                            )}
                            {dropoff && (
                                <Marker position={dropoff}>
                                    <Popup>Kote w prale</Popup>
                                </Marker>
                            )}
                        </MapContainer>
                    </div>

                </div>
            </div>
        </div>
    );
};
